import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

// Configure axios to send cookies with requests
axios.defaults.withCredentials = true;

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  avatar_url?: string;
  phone?: string;
  department?: string;
  position?: string;
  status: 'invited' | 'active' | 'inactive' | 'suspended';
  online_status?: 'online' | 'away' | 'busy' | 'offline';
  last_seen?: string;
  company_id: string;
  created_at?: string;
  updated_at?: string;
  role?: string;
  roles?: any[];
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  message?: string;
  user?: User;
  error?: string;
}

/**
 * Auth Service
 * Handles authentication using httpOnly cookies instead of localStorage
 */
class AuthService {

  /**
   * Login with email and password
   * Sets httpOnly cookies on success
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      console.log('🔐 Attempting login for:', credentials.email);

      const response = await axios.post(`${API_BASE_URL}/auth/login`, credentials, {
        withCredentials: true // Important: Send/receive cookies
      });

      console.log('✅ Login successful');
      return response.data;
    } catch (error: any) {
      console.error('❌ Login failed:', error.response?.data || error.message);
      throw error.response?.data || { success: false, error: 'Login failed' };
    }
  }

  /**
   * Logout and clear cookies
   */
  async logout(): Promise<void> {
    try {
      console.log('🔓 Logging out...');

      await axios.post(`${API_BASE_URL}/auth/logout`, {}, {
        withCredentials: true
      });

      console.log('✅ Logged out successfully');
    } catch (error: any) {
      console.error('❌ Logout error:', error.response?.data || error.message);
      // Even if logout fails, we continue (cookies might already be cleared)
    }
  }

  /**
   * Get current user info (requires auth cookie)
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      console.log('👤 Fetching current user...');

      const response = await axios.get(`${API_BASE_URL}/auth/me`, {
        withCredentials: true
      });

      if (response.data.success && response.data.user) {
        console.log('✅ User authenticated:', response.data.user.email);
        return response.data.user;
      }

      return null;
    } catch (error: any) {
      // 401 means not authenticated (normal case)
      if (error.response?.status === 401) {
        console.log('ℹ️ User not authenticated');
        return null;
      }

      console.error('❌ Error fetching user:', error.response?.data || error.message);
      return null;
    }
  }

  /**
   * Verify if user is authenticated
   */
  async verifyAuth(): Promise<boolean> {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/verify`, {
        withCredentials: true
      });

      return response.data.success && response.data.authenticated;
    } catch (error: any) {
      return false;
    }
  }

  /**
   * Refresh access token using refresh token cookie
   */
  async refreshToken(): Promise<boolean> {
    try {
      console.log('🔄 Refreshing token...');

      const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
        withCredentials: true
      });

      if (response.data.success) {
        console.log('✅ Token refreshed');
        return true;
      }

      return false;
    } catch (error: any) {
      console.error('❌ Token refresh failed:', error.response?.data || error.message);
      return false;
    }
  }

  /**
   * Setup axios interceptor to handle 401 responses
   * Automatically tries to refresh token on 401
   */
  setupAxiosInterceptor(onAuthFail: () => void) {
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Handle 403 CSRF token errors - refresh token and retry
        if (
          error.response?.status === 403 &&
          error.response?.data?.code === 'INVALID_CSRF_TOKEN' &&
          !originalRequest._csrfRetry
        ) {
          originalRequest._csrfRetry = true;
          try {
            const csrfResponse = await axios.get(`${API_BASE_URL}/auth/csrf-token`);
            if (csrfResponse.data?.csrfToken) {
              axios.defaults.headers.common['X-CSRF-Token'] = csrfResponse.data.csrfToken;
              originalRequest.headers['X-CSRF-Token'] = csrfResponse.data.csrfToken;
              console.log('🛡️ CSRF Token refreshed via interceptor');
              return axios(originalRequest);
            }
          } catch (csrfError) {
            console.warn('Failed to refresh CSRF token:', csrfError);
          }
          return Promise.reject(error);
        }

        // Don't retry if:
        // 1. Not a 401 error
        // 2. Already retried
        // 3. Is a refresh request itself (prevent infinite loop!)
        if (
          error.response?.status !== 401 ||
          originalRequest._retry ||
          originalRequest.url?.includes('/auth/refresh') ||
          originalRequest.url?.includes('/auth/login')
        ) {
          // If refresh endpoint fails, logout immediately
          if (originalRequest.url?.includes('/auth/refresh')) {
            console.log('❌ Refresh endpoint failed, logging out...');
            onAuthFail();
          }
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        console.log('⚠️ 401 detected, attempting token refresh...');

        // Try to refresh the token
        const refreshed = await this.refreshToken();

        if (refreshed) {
          // Retry the original request with new token
          return axios(originalRequest);
        } else {
          // Refresh failed, user needs to login again
          console.log('❌ Token refresh failed, logging out...');
          onAuthFail();
          return Promise.reject(error);
        }
      }
    );
  }
}

export const authService = new AuthService();
export default authService;
